import { Head, Link, router, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Manager {
  id: number
  name: string
}

interface StateOption {
  value: string
  label: string
}

interface CompanyData {
  id: number
  name: string
  nickname: string
  address: string
  address2: string
  city: string
  state: string
  zip: string
  phone: string
  email: string
  website: string
  color1: string
  color2: string
  manager_id: number | null
  status: number
}

interface Props {
  company: CompanyData
  managers: Manager[]
  states: StateOption[]
  errors?: Record<string, string[]>
}

export default function Edit({ company, managers, states, errors }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Manage", href: "/admin/companies" },
    { title: "Companies", href: "/admin/companies" },
    { title: "Edit", href: `/admin/companies/${company.id}/edit` },
  ]

  const { data, setData, patch, processing, transform } = useForm({
    name: company.name,
    nickname: company.nickname,
    address: company.address || "",
    address2: company.address2 || "",
    city: company.city || "",
    state: company.state || "",
    zip: company.zip || "",
    phone: company.phone || "",
    email: company.email || "",
    website: company.website || "",
    color1: company.color1 || "#000000",
    color2: company.color2 || "#000000",
    manager_id: company.manager_id ?? "",
    status: company.status,
  })

  transform((data) => ({ company: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/admin/companies/${company.id}`)
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Edit Company" />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Edit Company</h1>
          <button
            type="button"
            onClick={() => {
              if (confirm("Are you sure you want to delete this company?")) {
                router.delete(`/admin/companies/${company.id}`)
              }
            }}
            className="text-hpi-red hover:text-red-800"
            title="Delete Company"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="max-w-2xl">
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="mb-1 block font-medium">
                Company Name
              </label>
              <input
                type="text"
                id="name"
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={100}
                required
              />
              {errors?.name && (
                <p className="text-hpi-red mt-1 text-sm">
                  {errors.name.join(", ")}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="nickname" className="mb-1 block font-medium">
                Nickname
              </label>
              <input
                type="text"
                id="nickname"
                value={data.nickname}
                onChange={(e) => setData("nickname", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
                required
              />
              {errors?.nickname && (
                <p className="text-hpi-red mt-1 text-sm">
                  {errors.nickname.join(", ")}
                </p>
              )}
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="status" className="mb-1 block font-medium">
              Status
            </label>
            <select
              id="status"
              value={data.status}
              onChange={(e) => setData("status", Number(e.target.value))}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
            >
              <option value={0}>Active</option>
              <option value={1}>Inactive</option>
            </select>
          </div>

          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="address" className="mb-1 block font-medium">
                Address
              </label>
              <input
                type="text"
                id="address"
                value={data.address}
                onChange={(e) => setData("address", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
            <div>
              <label htmlFor="address2" className="mb-1 block font-medium">
                Address Line 2
              </label>
              <input
                type="text"
                id="address2"
                value={data.address2}
                onChange={(e) => setData("address2", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
          </div>

          <div className="mb-4 grid grid-cols-3 gap-4">
            <div>
              <label htmlFor="city" className="mb-1 block font-medium">
                City
              </label>
              <input
                type="text"
                id="city"
                value={data.city}
                onChange={(e) => setData("city", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
            <div>
              <label htmlFor="state" className="mb-1 block font-medium">
                State
              </label>
              <select
                id="state"
                value={data.state || ""}
                onChange={(e) => setData("state", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              >
                <option value="">Choose</option>
                {states.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="zip" className="mb-1 block font-medium">
                Zip
              </label>
              <input
                type="text"
                id="zip"
                value={data.zip}
                onChange={(e) => setData("zip", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={10}
              />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="manager_id" className="mb-1 block font-medium">
              Manager
            </label>
            <select
              id="manager_id"
              value={data.manager_id}
              onChange={(e) =>
                setData(
                  "manager_id",
                  e.target.value ? Number(e.target.value) : "",
                )
              }
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
            >
              <option value="">Choose</option>
              {managers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="phone" className="mb-1 block font-medium">
                Phone
              </label>
              <input
                type="text"
                id="phone"
                value={data.phone}
                onChange={(e) => setData("phone", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
            <div>
              <label htmlFor="email" className="mb-1 block font-medium">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={data.email}
                onChange={(e) => setData("email", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="website" className="mb-1 block font-medium">
              Website
            </label>
            <input
              type="text"
              id="website"
              value={data.website}
              onChange={(e) => setData("website", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              maxLength={100}
            />
          </div>

          <h3 className="mb-2 text-lg font-semibold">Layout</h3>
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="color1" className="mb-1 block font-medium">
                Color 1: {data.color1}
              </label>
              <input
                type="color"
                id="color1"
                value={data.color1}
                onChange={(e) => setData("color1", e.target.value)}
                className="h-[75px] w-[75px]"
              />
            </div>
            <div>
              <label htmlFor="color2" className="mb-1 block font-medium">
                Color 2: {data.color2}
              </label>
              <input
                type="color"
                id="color2"
                value={data.color2}
                onChange={(e) => setData("color2", e.target.value)}
                className="h-[75px] w-[75px]"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              Save
            </button>
            <Link
              href="/admin/companies"
              className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
